"use client";

/**
 * useCoachAudioEngine — supervisor-side playback for Twilio Media Streams.
 *
 * Extracted verbatim from LiveCalls.tsx (2026-07-24) so the monitoring UI can be
 * redesigned without touching the audio path. Every guard in here exists because
 * of a specific production bug; the comments name them. Do not "simplify" this
 * without reproducing the symptom first.
 *
 *   Twilio (both_tracks)                    this hook
 *   ────────────────────                    ─────────
 *   inbound  frames ─┐                  ┌─► inAcc  ─┐
 *                    ├─ socket 'audio' ─┤           ├─ mix(sum+clip) ─┐
 *   outbound frames ─┘                  └─► outAcc ─┘                 │
 *                                                                     ▼
 *                            gain ─► analyser ─► destination   (timeline-scheduled)
 *                                       │
 *                                       └─► RMS ─► onLevel() for the waveform
 */

import { useCallback, useRef } from "react";

// G.711 mu-law decoding table (ITU-T standard).
// A previous non-standard formula amplified low-amplitude samples ~15× more
// than it should, so the noise floor exploded while signal stayed roughly
// right — audio sounded "staticy". This is the reference formula.
const MULAW_TABLE = new Int16Array(256);
(() => {
  for (let i = 0; i < 256; i++) {
    const mu = ~i & 0xff;
    const sign = mu & 0x80;
    const exponent = (mu >> 4) & 0x07;
    const mantissa = mu & 0x0f;
    let sample = ((mantissa << 3) + 0x84) << exponent;
    sample -= 0x84;
    MULAW_TABLE[i] = sign ? -sample : sample;
  }
})();

function decodeMulaw(base64Payload: string): Float32Array {
  const raw = atob(base64Payload);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  const pcm = new Float32Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    pcm[i] = MULAW_TABLE[bytes[i]] / 32768;
  }
  return pcm;
}

// Append PCM samples to a per-track accumulator (immutable grow). Frames are
// tiny (~160 samples @ 50/sec/track) so the copy cost is negligible.
function concatSamples(prev: Float32Array, next: Float32Array): Float32Array {
  if (prev.length === 0) return next;
  const merged = new Float32Array(prev.length + next.length);
  merged.set(prev, 0);
  merged.set(next, prev.length);
  return merged;
}

export interface CoachAudioGraph {
  audioCtx: AudioContext;
}

export interface CoachAudioEngine {
  /** Create the AudioContext + gain + analyser graph. Call inside a user gesture. */
  createGraph: (volumePct: number, onLevel: (level0to1: number) => void) => CoachAudioGraph;
  /**
   * Pull the CURRENT frequency spectrum straight off the analyser, no React
   * state involved. The visualizer calls this from its own rAF loop and paints
   * to a canvas — pushing 60fps updates through a context that wraps the whole
   * authenticated app would re-render everything 60×/sec.
   *
   * Returns the number of usable bins written into `out`, or 0 if there's no
   * live graph.
   */
  readSpectrum: (out: Uint8Array<ArrayBuffer>) => number;
  /** Bin count to size the caller's buffer (analyser.frequencyBinCount). */
  getBinCount: () => number;
  /** Feed one Twilio media frame. `track` is 'inbound' | 'outbound'. */
  pushFrame: (track: string, base64Payload: string) => void;
  /** Live volume change (0..100). */
  applyVolume: (volumePct: number) => void;
  /** Fully tear down: cancel rAF, close context, reset buffers. Awaitable. */
  teardown: () => Promise<void>;
  /** Current AudioContext, or null. Exposed for the unmount keepalive path. */
  getAudioContext: () => AudioContext | null;
  /**
   * Route playback to a specific output device (headphones). Web Audio has no
   * sink selection of its own, so when a device is chosen we render into a
   * MediaStreamAudioDestinationNode, attach that stream to a hidden <audio>
   * element, and call setSinkId() on the element. Passing null returns to the
   * default output (straight to ctx.destination).
   *
   * Resolves false when the browser doesn't implement setSinkId (Safari/Firefox)
   * so the UI can say so instead of silently doing nothing.
   */
  setOutputDevice: (deviceId: string | null) => Promise<boolean>;
}

export function useCoachAudioEngine(): CoachAudioEngine {
  // Two separate sample FIFOs — one per Twilio track. The stream is attached
  // with `track: 'both_tracks'`, so customer (inbound) and agent (outbound)
  // arrive as SEPARATE frame streams. They must be MIXED (summed), not
  // concatenated, or audio comes out interleaved/garbled and ~2× too fast.
  const inAccRef = useRef<Float32Array>(new Float32Array(0));
  const outAccRef = useRef<Float32Array>(new Float32Array(0));
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const levelRafRef = useRef<number | null>(null);
  // Output-device routing: gain → analyser → (destination | sinkNode → <audio>)
  const sinkNodeRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const sinkElRef = useRef<HTMLAudioElement | null>(null);
  const outputDeviceIdRef = useRef<string | null>(null);
  // Timeline-scheduled playback cursor. Chaining via `source.onended` added a
  // micro-gap between every 20ms frame (hundreds per minute = crackly audio).
  // Scheduling against the AudioContext clock concatenates frames seamlessly.
  const nextStartTimeRef = useRef<number>(0);
  const droppedFramesRef = useRef<number>(0);

  const scheduleQueuedFrames = useCallback(() => {
    const ctx = audioContextRef.current;
    // Closed-context guard: a frame can land during teardown (packet in flight
    // when the user clicks Stop). createBuffer on a closed context throws.
    if (!ctx || ctx.state === "closed") return;
    // Context can go 'suspended' on tab blur and silently drop playback.
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => { /* ignore */ });
    }
    const gain = gainNodeRef.current;
    const SAFETY_MARGIN = 0.02; // 20ms — never schedule in the past
    const FRAME = 160;          // 20ms @ 8kHz — the mix granularity
    // LATENCY BUDGET (tightened 2026-07-25). These were 4000/8000 (0.5s/1s),
    // which let a full SECOND of audio queue before trimming — the supervisor
    // heard the call a beat behind, and whisper felt laggy because their reply
    // landed on top of stale context.
    //
    // For live coaching, dropping audio beats delay: you need to react to what
    // is being said NOW. 1200 samples = 150ms before we give up waiting on a
    // stalled track; 2400 = 300ms hard ceiling on queue depth.
    //
    // Floor on this path is ~200ms end to end (Twilio media → Render → WS →
    // browser → Web Audio scheduling), so 300ms is close to the practical
    // minimum; going lower starts dropping audible speech on normal jitter.
    const MAX_SOLO = 1200;      // 150ms — if one track is dead, stop waiting
    const MAX_BACKLOG = 2400;   // 300ms — drop oldest if we fall too far behind

    // Bounded-latency guard: on a network burst / tab-background catch-up, trim
    // BOTH tracks equally and re-anchor so latency stays low. For live
    // monitoring, dropped audio beats an ever-growing delay.
    const backlog = Math.min(inAccRef.current.length, outAccRef.current.length);
    if (backlog > MAX_BACKLOG) {
      const trim = backlog - MAX_BACKLOG;
      inAccRef.current = inAccRef.current.slice(trim);
      outAccRef.current = outAccRef.current.slice(trim);
      droppedFramesRef.current += Math.ceil(trim / FRAME);
      nextStartTimeRef.current = ctx.currentTime + SAFETY_MARGIN;
    }

    const emit = (pcm: Float32Array) => {
      const buffer = ctx.createBuffer(1, pcm.length, 8000);
      buffer.getChannelData(0).set(pcm);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      if (gain) source.connect(gain); else source.connect(ctx.destination);
      const now = ctx.currentTime;
      // Re-anchor if we've fallen behind real time (tab background / GC pause);
      // otherwise every subsequent frame schedules in the past and is dropped.
      let startAt = nextStartTimeRef.current;
      if (startAt < now + SAFETY_MARGIN) startAt = now + SAFETY_MARGIN;
      source.start(startAt);
      nextStartTimeRef.current = startAt + buffer.duration;
    };

    // Main path: while BOTH tracks have a full frame, MIX them (sum + soft-clip).
    // A real phone call is the two legs summed; this reproduces it cleanly and
    // keeps the tracks time-aligned (Twilio sends both at the same cadence).
    while (inAccRef.current.length >= FRAME && outAccRef.current.length >= FRAME) {
      const inA = inAccRef.current;
      const outA = outAccRef.current;
      const mixed = new Float32Array(FRAME);
      for (let i = 0; i < FRAME; i++) {
        let s = inA[i] + outA[i];
        if (s > 1) s = 1; else if (s < -1) s = -1;
        mixed[i] = s;
      }
      inAccRef.current = inA.slice(FRAME);
      outAccRef.current = outA.slice(FRAME);
      emit(mixed);
    }

    // Drift guard: if one track stalls while the other backs up past MAX_SOLO,
    // play the live track alone so audio never freezes.
    if (inAccRef.current.length >= MAX_SOLO && outAccRef.current.length < FRAME) {
      while (inAccRef.current.length >= FRAME) {
        emit(inAccRef.current.slice(0, FRAME));
        inAccRef.current = inAccRef.current.slice(FRAME);
      }
    } else if (outAccRef.current.length >= MAX_SOLO && inAccRef.current.length < FRAME) {
      while (outAccRef.current.length >= FRAME) {
        emit(outAccRef.current.slice(0, FRAME));
        outAccRef.current = outAccRef.current.slice(FRAME);
      }
    }
  }, []);

  const createGraph = useCallback(
    (volumePct: number, onLevel: (level: number) => void): CoachAudioGraph => {
      // Must be created after a user gesture OR explicitly resumed — browsers
      // start AudioContexts 'suspended' without a recent gesture, which
      // silently swallows playback. latencyHint 'interactive' optimizes the
      // graph for live monitoring rather than music.
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: 8000,
        latencyHint: "interactive",
      });
      // Stash SYNCHRONOUSLY so the socket 'audio' handler registered right
      // after this reads it immediately, before React's state update flushes.
      audioContextRef.current = audioCtx;
      if (audioCtx.state === "suspended") {
        audioCtx.resume().catch(() => { /* frames will retry */ });
      }
      // Reset the timeline for this session — a stale cursor from a previous
      // session schedules the first frames "in the future" (long silence).
      nextStartTimeRef.current = audioCtx.currentTime + 0.05;
      droppedFramesRef.current = 0;
      inAccRef.current = new Float32Array(0);
      outAccRef.current = new Float32Array(0);

      const gainNode = audioCtx.createGain();
      gainNode.gain.value = volumePct / 100;

      // AnalyserNode tap drives the waveform so bars react to who's actually
      // talking instead of a fixed pseudo-random pattern.
      // 1024 → 512 bins across 0–4kHz (Nyquist at 8kHz), ~7.8Hz per bin. Plenty
      // of resolution for a spectrum that actually dances on speech instead of
      // one lump. smoothing 0.72 keeps it fluid without going mushy.
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.72;
      analyser.minDecibels = -85;
      analyser.maxDecibels = -10;
      gainNode.connect(analyser);
      analyser.connect(audioCtx.destination);
      analyserRef.current = analyser;

      // COARSE level for React consumers (the widget pill), throttled to ~10fps.
      // The detailed visualizer does NOT use this — it reads the analyser
      // directly via readSpectrum() so nothing re-renders at frame rate.
      const buf = new Uint8Array(analyser.fftSize);
      let lastEmit = 0;
      const sampleLevel = () => {
        const now = performance.now();
        if (now - lastEmit >= 100) {
          lastEmit = now;
          analyser.getByteTimeDomainData(buf);
          let sumSq = 0;
          for (let i = 0; i < buf.length; i++) {
            const v = (buf[i] - 128) / 128;
            sumSq += v * v;
          }
          const rms = Math.sqrt(sumSq / buf.length);
          onLevel(Math.min(1, rms * 2.5));
        }
        levelRafRef.current = requestAnimationFrame(sampleLevel);
      };
      levelRafRef.current = requestAnimationFrame(sampleLevel);

      gainNodeRef.current = gainNode;

      // Re-apply a device the supervisor picked in an earlier session so their
      // headphone choice survives Listen → Whisper and reconnects.
      if (outputDeviceIdRef.current) {
        void applyOutputRouting(outputDeviceIdRef.current);
      }
      return { audioCtx };
    },
    [],
  );

  const pushFrame = useCallback(
    (track: string, base64Payload: string) => {
      const pcm = decodeMulaw(base64Payload);
      if (track === "outbound") {
        outAccRef.current = concatSamples(outAccRef.current, pcm);
      } else {
        inAccRef.current = concatSamples(inAccRef.current, pcm);
      }
      scheduleQueuedFrames();
    },
    [scheduleQueuedFrames],
  );

  /** (Re)wire the tail of the graph for the requested output device. */
  const applyOutputRouting = useCallback(async (deviceId: string | null): Promise<boolean> => {
    const ctx = audioContextRef.current;
    const analyser = analyserRef.current;
    if (!ctx || !analyser || ctx.state === 'closed') {
      outputDeviceIdRef.current = deviceId; // remember for the next createGraph
      return true;
    }

    // Detach whatever the tail currently feeds.
    try { analyser.disconnect(); } catch { /* ignore */ }
    if (sinkElRef.current) {
      try { sinkElRef.current.pause(); } catch { /* ignore */ }
      sinkElRef.current.srcObject = null;
      sinkElRef.current.remove();
      sinkElRef.current = null;
    }
    if (sinkNodeRef.current) {
      try { sinkNodeRef.current.disconnect(); } catch { /* ignore */ }
      sinkNodeRef.current = null;
    }

    if (!deviceId) {
      analyser.connect(ctx.destination);
      outputDeviceIdRef.current = null;
      return true;
    }

    const el = document.createElement('audio');
    if (typeof (el as any).setSinkId !== 'function') {
      // Safari / Firefox: no sink selection. Fall back to default output and
      // report so the caller can tell the user rather than silently ignoring.
      analyser.connect(ctx.destination);
      outputDeviceIdRef.current = null;
      return false;
    }

    const sink = ctx.createMediaStreamDestination();
    analyser.connect(sink);
    el.srcObject = sink.stream;
    el.autoplay = true;
    // Keep it out of the layout and out of the a11y tree — it's a routing
    // primitive, not a player.
    el.style.display = 'none';
    el.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el);

    try {
      await (el as any).setSinkId(deviceId);
      await el.play().catch(() => { /* autoplay policy — gesture already granted */ });
      sinkNodeRef.current = sink;
      sinkElRef.current = el;
      outputDeviceIdRef.current = deviceId;
      return true;
    } catch {
      // Device gone (headset unplugged) — revert to default rather than silence.
      try { sink.disconnect(); } catch { /* ignore */ }
      el.remove();
      analyser.connect(ctx.destination);
      outputDeviceIdRef.current = null;
      return false;
    }
  }, []);

  const setOutputDevice = useCallback(
    (deviceId: string | null) => applyOutputRouting(deviceId),
    [applyOutputRouting],
  );

  const applyVolume = useCallback((volumePct: number) => {
    if (gainNodeRef.current) gainNodeRef.current.gain.value = volumePct / 100;
  }, []);

  const teardown = useCallback(async () => {
    if (levelRafRef.current) {
      cancelAnimationFrame(levelRafRef.current);
      levelRafRef.current = null;
    }
    const ctx = audioContextRef.current;
    if (ctx) {
      // Resume first if suspended — close() on a suspended context can hang on
      // Safari; closing a running context resolves promptly.
      if (ctx.state === "suspended") {
        try { await ctx.resume(); } catch { /* ignore */ }
      }
      try { await ctx.close(); } catch { /* ignore */ }
    }
    if (sinkElRef.current) {
      try { sinkElRef.current.pause(); } catch { /* ignore */ }
      sinkElRef.current.srcObject = null;
      sinkElRef.current.remove();
      sinkElRef.current = null;
    }
    sinkNodeRef.current = null;
    audioContextRef.current = null;
    gainNodeRef.current = null;
    analyserRef.current = null;
    nextStartTimeRef.current = 0;
    droppedFramesRef.current = 0;
    inAccRef.current = new Float32Array(0);
    outAccRef.current = new Float32Array(0);
  }, []);

  const getAudioContext = useCallback(() => audioContextRef.current, []);

  const readSpectrum = useCallback((out: Uint8Array<ArrayBuffer>) => {
    const analyser = analyserRef.current;
    const ctx = audioContextRef.current;
    if (!analyser || !ctx || ctx.state === "closed") return 0;
    const n = Math.min(out.length, analyser.frequencyBinCount);
    analyser.getByteFrequencyData(out);
    return n;
  }, []);

  const getBinCount = useCallback(
    () => analyserRef.current?.frequencyBinCount ?? 0,
    [],
  );

  return { createGraph, pushFrame, applyVolume, teardown, getAudioContext, readSpectrum, getBinCount, setOutputDevice };
}
